import { api } from './api';
import type { TopicDetail } from './intelligence';

export interface BoardCard {
  id: number;
  tema: string;
  fonte: string | null;
  origem: string | null;
  metodo: string | null;
  estado: string;
  raia: 'produzir' | 'produzindo' | 'publicado' | string;
  responsavel: string | null;
  /** Autor da produção (quem clicou Produzir); null = geração automática. */
  produzido_por: string | null;
  erro: string | null;
  editoria: string | null;
  site: string | null;
  wp_post_id: number | null;
  wp_link: string | null;
  score: number | null;
  search_volume: number | null;
  increase_percentage: number | null;
  verdict: string | null;
  num_sources: number | null;
  tem_intel: boolean;
  /** B-01: quando o texto ficou pronto (revisão). Legado = null. */
  gerado_em: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BoardData {
  columns: {
    produzir: BoardCard[];
    produzindo: BoardCard[];
    publicado: BoardCard[];
  };
  total: number;
}

export interface BoardFilters {
  responsavel?: string;
  fonte?: string;
  metodo?: string;
  site?: string;
  dias?: number;
}

export interface BoardUser {
  email: string;
  nome: string;
}

export interface ProduzirBody {
  metodo: 'ia' | 'framing' | 'nrcs';
  framming?: string;
  site?: string;
  autopublish?: boolean;
  /** Framming de FONTES: URLs das matérias escolhidas — o gerador usa só elas. */
  source_links?: string[];
  /** 1 card → N produções: mantém o card no quadro e gera uma pauta-filha por ângulo. */
  manter_card?: boolean;
}

export interface SweepResult {
  dry_run: boolean;
  older_than_days: number;
  count: number;
  cards: { id: number; tema: string; estado: string; fonte: string | null; parado_desde: string | null }[];
}

export const boardApi = {
  get: (f: BoardFilters = {}): Promise<BoardData> => {
    const qs = new URLSearchParams();
    if (f.responsavel) qs.set('responsavel', f.responsavel);
    if (f.fonte) qs.set('fonte', f.fonte);
    if (f.metodo) qs.set('metodo', f.metodo);
    if (f.site) qs.set('site', f.site);
    if (f.dias) qs.set('dias', String(f.dias));
    const s = qs.toString();
    return api.get<BoardData>(`/board${s ? `?${s}` : ''}`);
  },
  /** Usuários ativos para o seletor "ver produção de" no Kanban. */
  usuarios: (): Promise<BoardUser[]> => api.get<BoardUser[]>('/board/usuarios'),
  enqueue: (body: {
    tema: string;
    fonte?: string;
    /** Marca (slug do site). OBRIGATORIO na sessao STAFF: sem ele o backend
     * devolve 422 {erro, motivo}, porque a sessao da casa nao tem client_id e o
     * card nasceria invisivel no quadro de todas as marcas. Em tenant vai
     * vazio — la o client_id da sessao resolve. */
    site?: string;
    editoria?: string;
    intel_id?: string | null; // B-13: handoff por referência (preferido)
    intel?: TopicDetail; // legado/fallback inline
  }): Promise<BoardCard> => api.post<BoardCard>('/board/from-intelligence', body),
  intel: (id: number): Promise<TopicDetail> => api.get<TopicDetail>(`/board/${id}/intel`),
  produzir: (id: number, body: ProduzirBody): Promise<BoardCard> =>
    api.post<BoardCard>(`/board/${id}/produzir`, body),
  retry: (id: number): Promise<BoardCard> => api.post<BoardCard>(`/board/${id}/retry`, {}),
  /** B-03: varredura de cards presos (>N dias, sem post). dry_run lista sem apagar. */
  sweep: (days: number, dryRun: boolean): Promise<SweepResult> =>
    api.post<SweepResult>(`/board/sweep?days=${days}&dry_run=${dryRun}`, {}),
  publicar: (id: number): Promise<BoardCard> => api.post<BoardCard>(`/board/${id}/publicar`, {}),
  remove: (id: number): Promise<{ deleted: boolean }> =>
    api.delete<{ deleted: boolean }>(`/board/${id}`),
  /** M3: este assunto já está publicado? Por quem? (fonte única dos badges) */
  estadoConteudo: (temas: string[]): Promise<Record<string, EstadoConteudo>> =>
    api.post<Record<string, EstadoConteudo>>('/board/estado-conteudo', { temas }),
  /** M2/M4: matérias que saíram do ar no WP sem destino de redirect. */
  aposentadoria: (): Promise<AposentadoriaItem[]> =>
    api.get<AposentadoriaItem[]>('/board/aposentadoria'),
  aposentadoriaDecidir: (
    pautaId: number,
    destino: string,
    motivo?: string,
  ): Promise<AposentadoriaItem> =>
    api.post<AposentadoriaItem>(`/board/aposentadoria/${pautaId}`, { destino, motivo }),
  aposentadoriaVerificar: (): Promise<{ verificadas: number; pendencias: number }> =>
    api.post<{ verificadas: number; pendencias: number }>('/board/aposentadoria/verificar', {}),
};

export interface EstadoConteudo {
  publicado: boolean;
  fonte?: string;
  quando?: string | null;
  wp_link?: string | null;
  tema_publicado?: string;
  estado_wp?: string;
}

export interface AposentadoriaItem {
  pauta_id: number;
  tema: string;
  fonte: string;
  wp_site?: string | null;
  wp_post_id?: string | number | null;
  wp_link?: string | null;
  estado_wp?: string;
  estado_wp_em?: string | null;
  aposentadoria?: { destino: string; motivo?: string | null } | null;
}
